import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Container from "@/components/base/Container";
import SectionHeading from "@/components/feature/SectionHeading";
import FilmCard from "@/components/feature/FilmCard";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/feature/EmptyState";
import DetailHero from "@/pages/detail/components/DetailHero";
import { films } from "@/mocks/films";
import { news } from "@/mocks/news";
import type { Film, NewsArticle } from "@/types/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";

export default function Detail() {
  const { id } = useParams();
  const filmList = films as Film[];
  const newsList = news as NewsArticle[];
  const film = filmList.find((item) => item.id === id);

  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const metaTitle = film
    ? `【${film.title}】剧情介绍与资讯 - 夜勤病栋影视大全`
    : "内容不存在 - 夜勤病栋影视大全";
  const metaDescription = film
    ? `详细介绍夜勤病栋相关的影视作品【${film.title}】，包含剧情摘要、演员资料及最新资讯。`
    : "该影视作品内容不存在或已删除。";
  useDocumentMeta(metaTitle, metaDescription);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const relatedFilms = useMemo(() => {
    if (!film) return [];
    return filmList
      .filter((item) => item.id !== film.id)
      .map((item) => {
        const genreOverlap = item.genres.filter((g) => film.genres.includes(g)).length;
        const tagOverlap = item.tags.filter((t) => film.tags.includes(t)).length;
        const score =
          genreOverlap * 3 + tagOverlap * 2 + (item.region === film.region ? 1 : 0) + (item.type === film.type ? 1 : 0);
        return { item, score };
      })
      .sort((a, b) => b.score - a.score || b.item.views - a.item.views)
      .slice(0, 4)
      .map((entry) => entry.item);
  }, [film, filmList]);

  const relatedNews = useMemo(() => {
    if (!film) return [];
    const direct = newsList.filter((item) => item.relatedFilmId === film.id);
    if (direct.length >= 2) return direct.slice(0, 3);
    const extra = newsList.filter(
      (item) =>
        item.relatedFilmId !== film.id &&
        item.tags.some((tag) => film.tags.includes(tag) || tag === film.title)
    );
    return [...direct, ...extra].slice(0, 3);
  }, [film, newsList]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制，可粘贴分享给好友");
    } catch {
      showToast(`分享链接：${url}`);
    }
  };

  if (!film) {
    return (
      <Container className="py-16 md:py-24">
        <EmptyState
          icon="ri-file-unknow-line"
          title="内容不存在或已删除"
          description="没有找到对应的影视作品，它可能已被移除或链接有误。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/");
          }}
        />
      </Container>
    );
  }

  const favorited = isFavorite(film.id);

  return (
    <div>
      <DetailHero film={film} />

      <Container className="py-8 md:py-10">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category?type=${film.type}`} className="hover:text-primary-700 transition-colors">
            {film.type}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800 line-clamp-1">{film.title}</span>
        </nav>

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button
            type="button"
            onClick={() => {
              const now = toggleFavorite(film.id);
              showToast(now ? "已加入我的收藏" : "已取消收藏");
            }}
            className={`flex items-center gap-2 px-4 h-11 rounded-md text-sm border transition-colors whitespace-nowrap cursor-pointer ${
              favorited
                ? "border-accent-500 bg-accent-500 text-background-50"
                : "border-background-300 bg-background-50 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
            }`}
          >
            <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
            {favorited ? "已收藏" : "收藏"}
          </button>
          <button
            type="button"
            onClick={handleShare}
            className="flex items-center gap-2 px-4 h-11 rounded-md text-sm border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-share-forward-line"></i>
            分享链接
          </button>
          <Link
            to={`/category?type=${film.type}`}
            className="flex items-center gap-2 px-4 h-11 rounded-md text-sm bg-primary-600 text-background-50 hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-apps-2-line"></i>
            进入{film.type}分类
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          <div className="flex-1 min-w-0">
            <section>
              <h2 className="flex items-center gap-2.5 mb-4">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                <span className="text-lg md:text-xl font-heading font-bold text-foreground-950">剧情简介</span>
              </h2>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <p className="text-sm md:text-[15px] text-foreground-700 leading-loose whitespace-pre-line">
                  {film.synopsis}
                </p>
              </div>
            </section>

            <section className="mt-10">
              <SectionHeading title="相关资讯" subtitle={`与《${film.title}》相关的文章与解析`} />
              {relatedNews.length > 0 ? (
                <div className="space-y-4">
                  {relatedNews.map((article) => (
                    <NewsCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <p className="py-12 text-center text-sm text-foreground-500 border border-dashed border-background-300 rounded-lg">
                  暂无相关资讯
                </p>
              )}
            </section>
          </div>

          <aside className="w-full lg:w-80 shrink-0 space-y-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h3 className="flex items-center gap-2 mb-4">
                <span className="w-1 h-4 rounded-full bg-accent-500"></span>
                <span className="font-heading font-bold text-sm text-foreground-950">目录导航</span>
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-foreground-400 mb-2">所属分类</p>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/category?type=${film.type}`}
                      className="px-3 h-8 flex items-center rounded-md text-xs bg-primary-100 text-primary-800 hover:bg-primary-200 transition-colors"
                    >
                      {film.type}
                    </Link>
                    {film.genres.map((genre) => (
                      <Link
                        key={genre}
                        to={`/category?genre=${encodeURIComponent(genre)}`}
                        className="px-3 h-8 flex items-center rounded-md text-xs bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
                      >
                        {genre}
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-foreground-400 mb-2">地区与年份</p>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/category?region=${encodeURIComponent(film.region)}`}
                      className="px-3 h-8 flex items-center rounded-md text-xs bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
                    >
                      {film.region}
                    </Link>
                    <Link
                      to={`/category?year=${film.year}`}
                      className="px-3 h-8 flex items-center rounded-md text-xs bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
                    >
                      {film.year} 年
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h3 className="flex items-center gap-2 mb-4">
                <span className="w-1 h-4 rounded-full bg-accent-500"></span>
                <span className="font-heading font-bold text-sm text-foreground-950">相关推荐</span>
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {relatedFilms.map((item) => (
                  <FilmCard key={item.id} film={item} />
                ))}
              </div>
            </section>
          </aside>
        </div>
      </Container>

      {toast && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-md bg-foreground-950 text-background-50 text-sm max-w-[90vw] truncate">
          {toast}
        </div>
      )}
    </div>
  );
}